import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Collapse } from 'react-collapse';
import {
  Spinner,
  ShowLessIcon,
  MailIcon,
  Title,
  BodyText
} from '@kvdbil/components';
import Link from '~/App/shared/components/Link';
import { updateSubscriptions } from '~/helpers/orchestration/subscribe';
import { QueryParams } from '../types';
import { requestErrorHandler } from '~/helpers/notifyError';
import { useSelector } from 'react-redux';
import { session as sessionSelector } from '~/App/shared/selectors/sessionSelector';
import { SubscriptionType } from '~/App/shared/interfaces/EmailSubscription';
import SubscriptionManagementForm from '~/App/views/Unsubscribe/components/SubscriptionManagementForm';
import StatusPageError from '~/App/shared/components/StatusPageError';
import { useCmsData } from '~/App/shared/hooks/useCmsData';
import {
  EmailSubscriptionManagementDocument,
  EmailSubscriptionManagementQuery
} from '~/config/generated/graphql';
import { EmailSubscriptionsData } from '~/App/shared/interfaces/store/CmsData';
import { getFormattedText } from '~/helpers/cmsDatoUtils';

type UnsubscribeProps = {
  queryParams: QueryParams;
};

declare global {
  interface Window {
    grecaptcha: {
      execute(
        siteKey: string,
        config: Record<string, unknown>
      ): Promise<string>;
      ready(callback: () => void): void;
    };
  }
}

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.light};
  width: 100%;
  height: 100%;
  display: flex;
  flex-grow: 1;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: calc(580px + 3rem);
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 0 auto;
  padding: 1.5rem 1.5rem 0;
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const width = '2.5rem';

const StyledMailIcon = styled(MailIcon)`
  color: ${({ theme }) => theme.colors.text.dark};
  height: ${width};
  width: ${width};
`;

const CollapseWrapper = styled.div`
  position: relative;
`;

const ToggleTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontAlternativeFamily};
  font-style: normal;
  font-weight: ${({ theme }) => theme.typography.fontAlternativeBoldWeight};
  font-size: 1rem;

  cursor: pointer;

  margin-top: 1.5rem;
  margin-bottom: 1rem;

  &.open {
    & svg {
      transform: rotate(0deg);
    }
  }

  & svg {
    margin-left: 1.5rem;
    transition: transform 300ms;
    transform: rotate(180deg);
  }
`;

const OtherSubscriptionsDescription = styled.p`
  margin-bottom: 0.6rem;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.grayscaleToned.light4};
`;

const MyPagesHint = styled.span`
  margin-bottom: 1.5rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 3rem 0;

  & p {
    text-align: center;
  }
`;

const InitialSpinner = styled(Spinner)`
  margin-bottom: 1.5rem;
  margin-top: 0.5rem;
  margin-left: auto;
  margin-right: auto;
`;

const SubscriptionManagementFormWrapper = styled.div`
  margin: 1rem 0;
`;

const FooterText = styled(BodyText)`
  margin-right: 0.5rem;
`;

const Unsubscribe = ({ queryParams }: UnsubscribeProps) => {
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [initialUnsubscribeDone, setInitialUnsubscribeDone] = useState(false);
  const session = useSelector(sessionSelector);
  const toggleOpen = () => setPreferencesOpen(open => !open);
  const queryHash = queryParams?.hash;
  const selectedType = queryParams?.type;
  const showSubscriptionList = selectedType !== SubscriptionType.SavedSearch;
  const isLoggedIn = session.isAuthenticated;
  const [isError, setIsError] = useState(!selectedType || !queryHash);

  const { data: cmsData } = useCmsData<
    EmailSubscriptionManagementQuery,
    EmailSubscriptionsData
  >({
    storeKey: 'emailSubscriptions',
    query: EmailSubscriptionManagementDocument
  });

  const {
    inProgressTitle,
    doneTitle,
    doneConfirmation,
    showMore,
    subscriptionListDescription,
    footerText,
    footerLinkText
  } = cmsData?.unsubscribePage ?? {};

  const currentSubscriptionLabel = useMemo(() => {
    return cmsData?.subscriptions?.find(
      subscription => subscription.subscriptionType === selectedType
    )?.checkboxLabel;
  }, [selectedType, cmsData?.subscriptions]);

  const submitSubscriptions = async (
    hash: string,
    optIn: SubscriptionType[],
    optOut: SubscriptionType[]
  ) => {
    try {
      await updateSubscriptions({ hash, optIn, optOut });
      setInitialUnsubscribeDone(true);
    } catch (e) {
      setIsError(true);
      requestErrorHandler(e);
    }
  };

  useEffect(() => {
    if (selectedType && queryHash) {
      void submitSubscriptions(queryHash, [], [selectedType]);
    }
  }, [queryHash, selectedType]);

  return (
    <>
      {isError && <StatusPageError />}
      {!isError && selectedType && (
        <Container>
          <Wrapper>
            {!initialUnsubscribeDone ? (
              <LoadingContainer>
                <Title>{inProgressTitle}</Title>
                <InitialSpinner size="small" />
              </LoadingContainer>
            ) : (
              <>
                <TopContainer>
                  <StyledMailIcon />
                  <Title>{doneTitle}</Title>
                </TopContainer>
                {doneConfirmation && currentSubscriptionLabel && (
                  <BodyText>
                    {getFormattedText(
                      doneConfirmation,
                      currentSubscriptionLabel
                    )}
                  </BodyText>
                )}
                {showSubscriptionList && (
                  <div>
                    <ToggleTitle
                      onClick={toggleOpen}
                      className={preferencesOpen ? 'open' : ''}
                    >
                      {showMore}
                      <ShowLessIcon />
                    </ToggleTitle>
                    <Collapse isOpened={preferencesOpen}>
                      <CollapseWrapper>
                        <OtherSubscriptionsDescription>
                          {subscriptionListDescription}
                        </OtherSubscriptionsDescription>
                        <SubscriptionManagementFormWrapper>
                          <SubscriptionManagementForm
                            hash={queryHash}
                            subscriptions={cmsData?.subscriptions}
                          />
                        </SubscriptionManagementFormWrapper>
                      </CollapseWrapper>
                    </Collapse>
                  </div>
                )}
                {isLoggedIn && (
                  <>
                    <Divider />
                    <MyPagesHint>
                      <FooterText as="span">{footerText}</FooterText>
                      <Link
                        to={`/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_SETTINGS`}
                      >
                        {footerLinkText}
                      </Link>
                    </MyPagesHint>
                  </>
                )}
              </>
            )}
          </Wrapper>
        </Container>
      )}
    </>
  );
};

export default Unsubscribe;
